import React from 'react';

import {
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import SearchProductRow from './SearchProductRow';

import type RefundLineItem from 'components/routes/Pages/Refund/models/RefundLineItem';
import { useTranslation } from 'react-i18next';
import type RefundProductDto from '../../../models/RefundProductDto';

interface ProductsTableProps {
    items: RefundProductDto[];
    onSelectChange: (value: boolean, refundProduct: RefundProductDto) => void;
    rowsPerPage: number;
    page: number;
    onChangePage: (
        _: React.MouseEvent<HTMLButtonElement> | null,
        page: number
    ) => void;
    onChangeRowsPerPage: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    loading: boolean;
    totalCount: number;
    cache: Map<RefundLineItem['itemNo'], RefundLineItem>;
}

const ProductsTable: React.FC<ProductsTableProps> = (props) => {
    const {
        items,
        onChangePage,
        onChangeRowsPerPage,
        onSelectChange,
        page,
        rowsPerPage,
        loading,
        totalCount,
        cache,
    } = props;

    const { t } = useTranslation('refund', {
        keyPrefix: 'PRODUCTS_STEP.ADD_PRODUCT_MODAL.SEARCH_PRODUCTS_TABLE',
    });

    const theme = useTheme();

    const small = useMediaQuery(theme.breakpoints.down('sm'));

    const labelRowsPerPage = small ? '' : t('ROWS_PER_PAGE');

    const labelDisplayedRows = small
        ? ({ from, to }) => (
              <Typography>
                  {from} - {to}
              </Typography>
          )
        : undefined;

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="body1">
                                    {t('TABLE_HEAD.PRODUCT')}
                                </Typography>
                            </TableCell>
                            <TableCell colSpan={2}>
                                <Typography variant="body1">
                                    {t('TABLE_HEAD.ACCOUNT_IDENTIFIER')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Stack alignItems="center" paddingY={2}>
                                        <Typography variant="caption">
                                            {t('PRODUCTS_LOADING')}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )}

                        {items?.map((item: RefundProductDto, index) => (
                            <SearchProductRow
                                key={index}
                                item={item}
                                onSelectChange={onSelectChange}
                                selected={cache.has(item.itemNo)}
                            />
                        ))}

                        {!loading && items?.length < 1 && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Stack alignItems="center" paddingY={2}>
                                        <Typography variant="caption">
                                            {t('EMPTY_STATE_TEXT')}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 100]}
                component="div"
                count={totalCount ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                labelRowsPerPage={labelRowsPerPage}
                labelDisplayedRows={labelDisplayedRows}
            />
        </>
    );
};

export default ProductsTable;
